import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase.js';
import { fetchProfile } from '../lib/profile.js';
import { fetchCurrentPair, fetchPairStats, upsertPairStats } from '../lib/pairs.js';
import {
  fetchTasks, createTask as dbCreateTask, updateTask as dbUpdateTask,
  deleteTask as dbDeleteTask, completeTask as dbCompleteTask, fetchRecentCompletions
} from '../lib/tasks.js';
import { subscribeToTasks, subscribeToTable } from '../lib/realtime.js';
import { calculatePoints, getNextDueDate, todayStr } from '../lib/dates.js';

/**
 * The main data hook for the app.
 * Provides all the state and actions the UI needs.
 *
 * In the prototype this was a giant useState'd thing. Now it's
 * Supabase-backed with real-time updates.
 */
export function useAppData(user, callbacks = {}) {
  const [profile, setProfile] = useState(null);
  const [pair, setPair] = useState(null);
  const [partner, setPartner] = useState(null);
  const [pairStats, setPairStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initial load
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [profileData, pairResult, taskList, completionList] = await Promise.all([
          fetchProfile(user.id),
          fetchCurrentPair(),
          fetchTasks(),
          fetchRecentCompletions(100),
        ]);

        if (cancelled) return;

        setProfile(profileData);
        setPair(pairResult.pair);
        setPartner(pairResult.partner);
        setTasks(taskList);
        setCompletions(completionList);

        if (pairResult.pair) {
          const stats = await fetchPairStats(pairResult.pair.id);
          if (!cancelled) setPairStats(stats || { loveScore: 0, streakDays: 0 });
        }
      } catch (e) {
        console.error('useAppData load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  // Real-time subscription to tasks
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToTasks(({ event, row, old }) => {
      if (event === 'INSERT') {
        setTasks(prev => prev.find(t => t.id === row.id) ? prev : [row, ...prev]);
      } else if (event === 'UPDATE') {
        // Detect: did partner just complete one of MY tasks?
        if (row.completed && !old?.completed && row.createdBy === user.id && row.assignedTo && row.assignedTo !== user.id) {
          if (callbacks.onPartnerCompletion) {
            callbacks.onPartnerCompletion(row);
          }
        }
        setTasks(prev => prev.map(t => t.id === row.id ? row : t).filter(t => !t.completed));
      } else if (event === 'DELETE') {
        setTasks(prev => prev.filter(t => t.id !== row.id));
      }
    });
    return unsubscribe;
  }, [user, callbacks.onPartnerCompletion]);

  // Real-time subscription to notifications (for invite-accepted etc.)
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToTable('notifications', async ({ event, row }) => {
      if (event !== 'INSERT') return;
      if (row.recipientId !== user.id) return;

      // Invite was accepted — reload pair data
      if (row.type === 'invite_accepted') {
        const pairResult = await fetchCurrentPair();
        setPair(pairResult.pair);
        setPartner(pairResult.partner);
        if (pairResult.pair) {
          const stats = await fetchPairStats(pairResult.pair.id);
          setPairStats(stats || { loveScore: 0, streakDays: 0 });
        }
        if (callbacks.onInviteAccepted) {
          callbacks.onInviteAccepted(row, pairResult.partner);
        }
      }
    });
    return unsubscribe;
  }, [user, callbacks.onInviteAccepted]);

  // Real-time subscription to pair_stats so both partners see score updates
  useEffect(() => {
    if (!user || !pair) return;
    const unsubscribe = subscribeToTable('pair_stats', ({ event, row }) => {
      if (!row || row.pairId !== pair.id) return;
      // Both INSERT and UPDATE update local state
      setPairStats(row);
    });
    return unsubscribe;
  }, [user, pair]);

  // Real-time subscription to completions so partner sees them
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToTable('completions', ({ event, row }) => {
      if (event !== 'INSERT' || !row) return;
      // Only add if it's relevant to me (RLS already filters, but extra safety)
      setCompletions(prev => prev.find(c => c.id === row.id) ? prev : [row, ...prev]);
    });
    return unsubscribe;
  }, [user]);

  // Actions
  const addTask = useCallback(async (taskData) => {
    const dataWithPair = {
      ...taskData,
      pairId: taskData.list !== 'private' ? pair?.id : null,
    };
    const newTask = await dbCreateTask(dataWithPair);
    setTasks(prev => prev.find(t => t.id === newTask.id) ? prev : [newTask, ...prev]);
    return newTask;
  }, [pair]);

  const updateTask = useCallback(async (taskId, updates) => {
    const updated = await dbUpdateTask(taskId, updates);
    setTasks(prev => prev.map(t => t.id === taskId ? updated : t));
    return updated;
  }, []);

  const deleteTask = useCallback(async (taskId) => {
    await dbDeleteTask(taskId);
    setTasks(prev => prev.filter(t => t.id !== taskId));
  }, []);

  const completeTask = useCallback(async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const completionISO = new Date().toISOString();
    const points = calculatePoints(task, completionISO);
    const isLate = task.deadline && new Date(task.deadline) < new Date(completionISO);
    const isEarly = task.deadline && (new Date(task.deadline) - new Date(completionISO)) > 86400000;

    // For recurring tasks, advance the deadline
    let nextDeadline = null;
    if (task.repeatType && task.repeatType !== 'none') {
      const baseDate = task.deadline ? task.deadline.split('T')[0] : todayStr();
      const dateForNext = baseDate < todayStr() ? todayStr() : baseDate;
      const next = getNextDueDate(dateForNext, task.repeatType, task.interval, task.monthDay, task.weekday, task.weekdays || task.repeatWeekdays);
      if (next) nextDeadline = `${next}T${task.deadline ? task.deadline.split('T')[1] : '20:00:00'}`;
    }

    await dbCompleteTask(taskId, {
      points,
      wasLate: isLate,
      wasEarly: isEarly,
      nextDeadline,
    });

    // Update local state
    if (task.repeatType && task.repeatType !== 'none' && nextDeadline) {
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, deadline: nextDeadline } : t));
    } else {
      setTasks(prev => prev.filter(t => t.id !== taskId));
    }

    // Note: We don't add the completion to local state here — the real-time
    // subscription on the 'completions' table will pick it up and add it.
    // This avoids a duplicate (optimistic + realtime) since they have different IDs.

    // Update pair stats if applicable
    if (pair && (task.list === 'sent' || task.list === 'shared')) {
      // Use the actual points awarded (from calculatePoints) for sent tasks.
      // For shared tasks, give a flat 3-point boost (since shared tasks don't have personal points).
      const loveBonus = task.list === 'sent' ? points : 3;
      const newScore = (pairStats?.loveScore || 0) + loveBonus;
      const today = todayStr();
      const newStreak = pairStats?.lastActiveDay === today
        ? pairStats.streakDays
        : (pairStats?.lastActiveDay && new Date(today) - new Date(pairStats.lastActiveDay) <= 86400000 * 1.5)
          ? (pairStats.streakDays || 0) + 1
          : 1;
      const updated = await upsertPairStats(pair.id, {
        love_score: newScore,
        streak_days: newStreak,
        last_active_day: today,
        total_completions: (pairStats?.totalCompletions || 0) + 1,
      });
      setPairStats(updated);
    }

    return { points, wasLate: isLate, wasEarly: isEarly, taskList: task.list };
  }, [tasks, user, pair, pairStats]);

  return {
    profile,
    pair,
    partner,
    pairStats,
    tasks,
    completions,
    loading,
    actions: {
      addTask,
      updateTask,
      deleteTask,
      completeTask,
    },
  };
}
